/**
 * Solana Service
 * Handles all Solana blockchain interactions
 *
 * Balance fetching uses backend proxy to avoid CORS and rate limiting
 * Transactions still use direct RPC via wallet adapter
 */

import {
  Connection,
  PublicKey,
  Transaction,
  LAMPORTS_PER_SOL,
  SendTransactionError,
  sendAndConfirmTransaction
} from '@solana/web3.js'
import {
  TOKEN_PROGRAM_ID,
  getAssociatedTokenAddress,
  createTransferInstruction,
  createAssociatedTokenAccountInstruction,
  createAssociatedTokenAccountIdempotentInstruction,
  getAccount,
} from '@solana/spl-token'
import { API_CONFIG, BLOCKCHAIN_CONFIG, APP_CONFIG } from '../config/app.config'
import { TOKEN_DECIMALS as CONFIGURED_DECIMALS } from '../config/tokenDecimals'

// Backend API URL for proxy endpoints
const API_URL = API_CONFIG.baseUrl

// Direct RPC URL (only used for transactions, not balance fetching)
const RPC_URL = BLOCKCHAIN_CONFIG.solana.rpcUrl

// Simulation mode - skips real blockchain transactions for testing
const SIMULATION_MODE = APP_CONFIG.simulationMode

// Token mint addresses - configured via environment variables (v3.2)
// Default to MAINNET tokens if not specified
const TAKARA_MINT = new PublicKey(
  import.meta.env.VITE_TAKARA_MINT || '8e2ieyBCgdx3254SBtVy5e3vuqgrK5C8Yq6UGt3DpGMq'
)
const USDT_MINT = new PublicKey(
  import.meta.env.VITE_USDT_MINT || 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB'
)
const LAIKA_MINT = new PublicKey(
  import.meta.env.VITE_LAIKA_MINT || 'Euoq6CyQFCjCVSLR9wFaUPDW19Y6ZHwEcJoZsEi643i1'
)

const TOKEN_DECIMALS: Record<string, number> = {
  [USDT_MINT.toBase58()]:   CONFIGURED_DECIMALS.USDT,
  [TAKARA_MINT.toBase58()]: CONFIGURED_DECIMALS.TAKARA,
  [LAIKA_MINT.toBase58()]:  CONFIGURED_DECIMALS.LAIKA,
}

// Local cache for immediate access (matches backend Redis cache of 5 min)
const CACHE_TTL = 300000 // 5 minutes local cache (same as backend Redis)
const balanceCache = new Map<string, { value: number; timestamp: number }>()

function getCachedBalance(key: string): number | null {
  const cached = balanceCache.get(key)
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.value
  }
  return null
}

function setCachedBalance(key: string, value: number): void {
  balanceCache.set(key, { value, timestamp: Date.now() })
}

/**
 * Fetch balance from backend proxy with retry logic
 * Falls back to 0 on any error (non-critical feature)
 * @param endpoint - API endpoint path
 * @param forceRefresh - If true, bypasses cache by adding ?refresh=true
 * @param retries - Number of retry attempts for rate limit errors
 */
async function fetchBalanceFromProxy(
  endpoint: string,
  forceRefresh: boolean = false,
  retries: number = 2
): Promise<number> {
  const url = forceRefresh
    ? `${API_URL}${endpoint}${endpoint.includes('?') ? '&' : '?'}refresh=true`
    : `${API_URL}${endpoint}`

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url)

      // Handle rate limiting with retry
      if (response.status === 429) {
        if (attempt < retries) {
          console.warn(`Balance fetch rate limited, retrying... (${attempt + 1}/${retries})`)
          // Wait with exponential backoff
          await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt)))
          continue
        }
        console.error('Balance fetch rate limited after all retries')
        return 0
      }

      if (!response.ok) {
        console.error(`Balance fetch failed: ${response.status}`)
        return 0
      }

      const data = await response.json()

      // Log warnings if backend reports errors
      if (data.partial && data.data?.errors) {
        console.warn('Some balances failed to fetch:', data.data.errors)
      }

      return data.success ? data.data.balance : 0
    } catch (error) {
      if (attempt < retries) {
        console.warn(`Balance fetch error, retrying... (${attempt + 1}/${retries})`)
        await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt)))
        continue
      }
      console.error('Balance fetch failed:', error)
      return 0
    }
  }

  return 0
}

// Type for sendTransaction function from wallet adapter
type SendTransactionFn = (
  transaction: Transaction,
  connection: Connection,
  options?: { skipPreflight?: boolean }
) => Promise<string>

class SolanaService {
  private connection: Connection

  constructor() {
    // HTTP goes through backend proxy (avoids CORS/403)
    // WebSocket endpoint is configurable via VITE_SOLANA_WS_URL
    // Falls back to public endpoint if not set (may be rate limited)
    const wsEndpoint = import.meta.env.VITE_SOLANA_WS_URL || 'wss://api.mainnet-beta.solana.com/'

    this.connection = new Connection(RPC_URL, {
      commitment: 'confirmed',
      wsEndpoint,
      disableRetryOnRateLimit: true,
    })
  }

  /**
   * Get SOL balance via backend proxy (with local caching)
   * @param forceRefresh - If true, bypasses both local and backend cache
   */
  async getBalance(publicKey: PublicKey, forceRefresh: boolean = false): Promise<number> {
    const address = publicKey.toBase58()
    const cacheKey = `sol:${address}`
    
    // If force refresh, clear local cache
    if (forceRefresh) {
      balanceCache.delete(cacheKey)
    } else {
      const cached = getCachedBalance(cacheKey)
      if (cached !== null) return cached
    }

    const balance = await fetchBalanceFromProxy(`/solana/balance/${address}`, forceRefresh)
    setCachedBalance(cacheKey, balance)
    return balance
  }

  /**
   * Get SPL token balance via backend proxy (with local caching)
   * @param forceRefresh - If true, bypasses cache and fetches fresh data
   */
  async getTokenBalance(
    walletPublicKey: PublicKey | string,
    mintPublicKey: PublicKey,
    forceRefresh: boolean = false
  ): Promise<number> {
    const address = typeof walletPublicKey === 'string' ? walletPublicKey : walletPublicKey.toBase58()
    const mint = mintPublicKey.toBase58()
    const cacheKey = `token:${address}:${mint}`
    
    // If force refresh, clear cache for this key
    if (forceRefresh) {
      balanceCache.delete(cacheKey)
    } else {
      const cached = getCachedBalance(cacheKey)
      if (cached !== null) return cached
    }

    // Determine token name for cleaner URL
    let tokenName = mint
    if (mint === USDT_MINT.toBase58()) tokenName = 'USDT'
    else if (mint === TAKARA_MINT.toBase58()) tokenName = 'TAKARA'
    else if (mint === LAIKA_MINT.toBase58()) tokenName = 'LAIKA'

    const balance = await fetchBalanceFromProxy(`/solana/token/${address}/${tokenName}`, forceRefresh)
    setCachedBalance(cacheKey, balance)
    return balance
  }

  /**
   * Clear balance cache (call after transactions)
   */
  clearCache(): void {
    balanceCache.clear()
  }

  /**
   * Clear cache for a specific wallet address
   */
  clearCacheForAddress(walletAddress: string | PublicKey): void {
    const address = typeof walletAddress === 'string' ? walletAddress : walletAddress.toBase58()
    
    // Clear all token balances for this address
    balanceCache.delete(`token:${address}:${USDT_MINT.toBase58()}`)
    balanceCache.delete(`token:${address}:${TAKARA_MINT.toBase58()}`)
    balanceCache.delete(`token:${address}:${LAIKA_MINT.toBase58()}`)
    balanceCache.delete(`sol:${address}`)
  }

  /**
   * Transfer multiple SPL tokens in a single transaction
   * Combines all token transfers into one transaction for efficiency
   * @param transfers - Array of {toPublicKey, mintPublicKey, amount}
   * @param fromPublicKey - Sender address
   * @param sendTransaction - Wallet adapter's sendTransaction function
   */
  async transferMultipleTokens(
    fromPublicKey: PublicKey,
    transfers: Array<{
      toPublicKey: PublicKey
      mintPublicKey: PublicKey
      amount: number
    }>,
    sendTransaction: SendTransactionFn
  ): Promise<string> {
    // SIMULATION MODE - Return fake signature for testing
    if (SIMULATION_MODE) {
      console.log('🧪 SIMULATION MODE: Skipping real blockchain transaction (batch)')
      console.log(`   From: ${fromPublicKey.toBase58()}`)
      transfers.forEach((t, i) => {
        console.log(`   Transfer ${i + 1}: ${t.amount} of ${t.mintPublicKey.toBase58()} to ${t.toPublicKey.toBase58()}`)
      })

      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 1500))

      // Return fake signature
      const fakeSignature = `SIM_${Date.now()}_${Math.random().toString(36).substring(7)}`
      console.log(`   Fake Signature: ${fakeSignature}`)
      return fakeSignature
    }

    // Declare signature variable outside try block so it's accessible in catch
    let signature: string | undefined

    try {
      console.log('📦 Building transaction instructions...')

      // Build instructions FIRST (involves RPC calls that take time)
      const transaction = new Transaction()
      transaction.feePayer = fromPublicKey

      // Process each transfer
      for (const transfer of transfers) {
        const { toPublicKey, mintPublicKey, amount } = transfer

        // Get associated token accounts
        const fromTokenAccount = await getAssociatedTokenAddress(
          mintPublicKey,
          fromPublicKey
        )

        const toTokenAccount = await getAssociatedTokenAddress(
          mintPublicKey,
          toPublicKey
        )

        // Detect mobile to optimize RPC calls
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

        console.log(`📝 Transfer: ${amount} tokens`)
        console.log(`   From account: ${fromTokenAccount.toBase58()}`)
        console.log(`   To account: ${toTokenAccount.toBase58()}`)
        console.log(`   Signer: ${fromPublicKey.toBase58()}`)

        if (isMobile) {
          // On mobile: Always add IDEMPOTENT create instruction for destination
          // This avoids slow RPC calls AND won't fail if account already exists
          console.log('📱 Mobile detected: Adding idempotent create instruction (won\'t fail if exists)')
          transaction.add(
            createAssociatedTokenAccountIdempotentInstruction(
              fromPublicKey,
              toTokenAccount,
              toPublicKey,
              mintPublicKey
            )
          )
        } else {
          // On desktop: Check if destination account exists first
          console.log(`🔍 Checking destination token account: ${toTokenAccount.toBase58().slice(0, 8)}...`)
          try {
            await getAccount(this.connection, toTokenAccount)
            console.log('✅ Destination token account exists')
          } catch (error: any) {
            // Account doesn't exist - add instruction to create it
            console.log('➕ Destination token account does not exist, will create it')
            transaction.add(
              createAssociatedTokenAccountInstruction(
                fromPublicKey,
                toTokenAccount,
                toPublicKey,
                mintPublicKey
              )
            )
          }
        }

        // Add transfer instruction - use correct decimals for each token
        const decimals = TOKEN_DECIMALS[mintPublicKey.toBase58()] ?? 9 // Default to 9 for Solana tokens
        const transferAmount = Math.round(amount * Math.pow(10, decimals))

        console.log(`💸 Adding transfer: ${amount} tokens (${transferAmount} raw)`)

        transaction.add(
          createTransferInstruction(
            fromTokenAccount,
            toTokenAccount,
            fromPublicKey,
            transferAmount,
            [],
            TOKEN_PROGRAM_ID
          )
        )
      }

      console.log('✅ Transaction instructions built successfully')

      // Retry logic for transaction sending (in case of blockhash expiration)
      const MAX_RETRIES = 2
      let retryCount = 0
      let lastError: Error | null = null

      while (retryCount <= MAX_RETRIES) {
        try {
          // Get fresh blockhash RIGHT BEFORE sending to minimize expiration window
          // (moved after instruction building to avoid staleness during RPC calls + wallet confirmation)
          const { blockhash, lastValidBlockHeight } = await this.connection.getLatestBlockhash('confirmed')
          transaction.recentBlockhash = blockhash
          transaction.lastValidBlockHeight = lastValidBlockHeight

          console.log(`📤 Sending transaction (attempt ${retryCount + 1}/${MAX_RETRIES + 1})...`)

          // Send transaction with wallet adapter
          signature = await sendTransaction(transaction, this.connection, {
            skipPreflight: false,
          })

          console.log('✅ Transaction sent:', signature)
          console.log('⏳ Waiting for confirmation...')

          // Wait for confirmation with timeout (30 seconds max)
          const confirmationPromise = this.connection.confirmTransaction(
            {
              signature,
              blockhash,
              lastValidBlockHeight,
            },
            'confirmed'
          )

          // Add timeout to prevent infinite hanging
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Transaction confirmation timeout (30s)')), 30000)
          })

          const confirmation = await Promise.race([confirmationPromise, timeoutPromise]) as any

          if (confirmation?.value?.err) {
            const errorMsg = JSON.stringify(confirmation.value.err)

            // Check if it's a blockhash expiration error
            if (errorMsg.includes('block height exceeded') || errorMsg.includes('BlockhashNotFound')) {
              throw new Error(`Transaction expired: ${errorMsg}`)
            }

            throw new Error(`Transaction failed: ${errorMsg}`)
          }

          console.log('✅ Transaction confirmed!')

          // Success - break out of retry loop
          break

        } catch (retryError: any) {
          lastError = retryError

          // Check if error is retryable (blockhash expired)
          const isExpiredError = retryError.message?.includes('block height exceeded') ||
                                 retryError.message?.includes('BlockhashNotFound') ||
                                 retryError.message?.includes('Transaction expired')

          if (isExpiredError && retryCount < MAX_RETRIES) {
            console.warn(`⚠️ Transaction expired (attempt ${retryCount + 1}). Retrying with fresh blockhash...`)
            retryCount++
            // Wait a bit before retry
            await new Promise(resolve => setTimeout(resolve, 1000))
            continue
          }

          // If not retryable or out of retries, throw the error
          throw retryError
        }
      }

      // If we got here and still have an error, throw it
      if (lastError && !signature) {
        throw lastError
      }

      // Clear cache for sender's address after successful transaction
      // This ensures balance updates immediately after transfer
      this.clearCacheForAddress(fromPublicKey)

      return signature
    } catch (error: any) {
      console.error('❌ Batch token transfer error:', error)

      // CRITICAL FIX: Don't throw on confirmation timeout
      // Transaction was already sent and may have succeeded on-chain
      // Return signature so backend can verify transaction
      if (error.message?.includes('timeout') && signature) {
        console.warn('⚠️ Transaction confirmation timed out after 30 seconds')
        console.warn('💡 Transaction was sent successfully - signature:', signature)
        console.warn('🔄 Backend will verify on-chain status')

        // Clear cache to trigger balance refresh
        this.clearCacheForAddress(fromPublicKey)

        // Return signature anyway - let backend verify the transaction
        return signature
      }

      // Log more details about the error
      if (error.message?.includes('timeout')) {
        console.error('⏱️ Transaction confirmation timed out after 30 seconds')
        console.error('💡 This may be due to network congestion or slow RPC')
      }

      // Extract more detailed error info if available
      if (error instanceof SendTransactionError) {
        const logs = await error.getLogs(this.connection)
        console.error('📋 Transaction logs:', logs)
      }

      throw error
    }
  }

  /**
   * Transfer SPL tokens using wallet adapter's sendTransaction
   * This method handles blockhash management internally and avoids "Blockhash not found" errors
   * In SIMULATION_MODE, returns a fake signature without doing real transactions
   */
  async transferToken(
    fromPublicKey: PublicKey,
    toPublicKey: PublicKey,
    mintPublicKey: PublicKey,
    amount: number,
    sendTransaction: SendTransactionFn
  ): Promise<string> {
    // Use the batch transfer method with a single transfer
    return this.transferMultipleTokens(
      fromPublicKey,
      [{ toPublicKey, mintPublicKey, amount }],
      sendTransaction
    )
  }

  /**
   * Check if simulation mode is enabled
   */
  isSimulationMode(): boolean {
    return SIMULATION_MODE
  }

  /**
   * Transfer USDT
   */
  async transferUSDT(
    fromPublicKey: PublicKey,
    toPublicKey: PublicKey,
    amount: number,
    sendTransaction: SendTransactionFn
  ): Promise<string> {
    return this.transferToken(
      fromPublicKey,
      toPublicKey,
      USDT_MINT,
      amount,
      sendTransaction
    )
  }

  /**
   * Transfer TAKARA
   */
  async transferTAKARA(
    fromPublicKey: PublicKey,
    toPublicKey: PublicKey,
    amount: number,
    sendTransaction: SendTransactionFn
  ): Promise<string> {
    return this.transferToken(
      fromPublicKey,
      toPublicKey,
      TAKARA_MINT,
      amount,
      sendTransaction
    )
  }

  /**
   * Transfer LAIKA
   */
  async transferLAIKA(
    fromPublicKey: PublicKey,
    toPublicKey: PublicKey,
    amount: number,
    sendTransaction: SendTransactionFn
  ): Promise<string> {
    return this.transferToken(
      fromPublicKey,
      toPublicKey,
      LAIKA_MINT,
      amount,
      sendTransaction
    )
  }

  /**
   * Get the connection instance (for wallet adapter's sendTransaction)
   */
  getConnection(): Connection {
    return this.connection
  }

  /**
   * Get USDT balance
   * @param forceRefresh - If true, bypasses cache and fetches fresh data
   */
  async getUSDTBalance(walletPublicKey: PublicKey | string, forceRefresh: boolean = false): Promise<number> {
    return this.getTokenBalance(walletPublicKey, USDT_MINT, forceRefresh)
  }

  /**
   * Get TAKARA balance
   * @param forceRefresh - If true, bypasses cache and fetches fresh data
   */
  async getTAKARABalance(walletPublicKey: PublicKey | string, forceRefresh: boolean = false): Promise<number> {
    return this.getTokenBalance(walletPublicKey, TAKARA_MINT, forceRefresh)
  }

  /**
   * Get LAIKA balance
   * @param forceRefresh - If true, bypasses cache and fetches fresh data
   */
  async getLAIKABalance(walletPublicKey: PublicKey | string, forceRefresh: boolean = false): Promise<number> {
    return this.getTokenBalance(walletPublicKey, LAIKA_MINT, forceRefresh)
  }

  /**
   * Get platform wallet address (where to send tokens)
   */
  getPlatformWalletAddress(): PublicKey {
    // Platform wallet address for receiving payments
    const walletAddress = import.meta.env.VITE_PLATFORM_WALLET_SOL

    if (!walletAddress) {
      throw new Error(
        'CRITICAL: VITE_PLATFORM_WALLET_SOL environment variable is not set. ' +
        'This is required for receiving payment tokens. Check your .env file.'
      )
    }

    return new PublicKey(walletAddress)
  }
}

export const solanaService = new SolanaService()
export default solanaService
